<template>
  <div class="team-dashboard">
    <MainNavbar />

    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />

    <!-- Main Content -->
    <main class="dashboard-main">
      <!-- Dashboard Header with Team Info and Tabs -->
      <TeamHeader
        :team="currentTeam"
        :user-role="userRole"
        :active-tab="activeTab"
        :tabs="tabs"
        @image-error="handleImageError"
        @edit-team="editTeam"
        @invite-players="invitePlayers"
        @delete-team="confirmDeleteTeam"
        @tab-change="activeTab = $event"
      />

      <!-- Dashboard Content -->
      <div class="dashboard-content">
        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'" class="tab-content">
          <!-- Stats Cards -->
          <StatsCards
            :upcoming-matches-count="upcomingMatches.length"
            :upcoming-match-rate="upcomingMatchRate"
            :win-rate="winRate"
            :win-trend="winTrend"
            :win-trend-icon="winTrendIcon"
            :win-trend-text="winTrendText"
            :active-players-count="activePlayersCount"
            :new-players-this-month="newPlayersThisMonth"
            :team-rating="teamRating"
            :rating-count="ratingCount"
          />

          <!-- Challenge requests (inline — football dashboard only) -->
          <section class="challenges-section" aria-label="Challenge requests">
            <div class="card">
              <div class="card-header">
                <div class="card-title">
                  <i class="fa-solid fa-handshake" aria-hidden="true"></i>
                  <h2>Challenge Requests</h2>
                  <span v-if="challenges.length > 0" class="badge-count">{{ challenges.length }}</span>
                </div>
                <button
                  v-if="challenges.length > 0"
                  type="button"
                  class="btn-text"
                  @click="refreshChallenges"
                >
                  <i class="fa-solid fa-sync-alt" aria-hidden="true"></i>
                  Refresh
                </button>
              </div>
              <div class="card-body">
                <div v-if="loadingChallenges" class="loading-state">
                  <div class="spinner-small" aria-hidden="true"></div>
                  <p>Loading challenges...</p>
                </div>
                <div v-else-if="challenges.length > 0" class="challenges-list">
                  <transition-group name="challenge-list">
                    <article
                      v-for="challenge in challenges"
                      :key="challenge.id"
                      class="challenge-card booking-card"
                    >
                      <div class="booking-header">
                        <div class="booking-id">
                          <i class="fa-solid fa-bell" aria-hidden="true"></i>
                          <span>Challenge #{{ challenge.id }}</span>
                        </div>
                        <div class="booking-meta">
                          <span v-if="challengeDate(challenge)" class="booking-date">
                            <i class="fa-solid fa-clock" aria-hidden="true"></i>
                            {{ formatDate(challengeDate(challenge)) }}
                          </span>
                          <span class="booking-status pending">Awaiting your response</span>
                        </div>
                      </div>
                      <div class="booking-body">
                        <div class="challenge-info">
                          <div class="challenger-team">
                            <div class="team-avatar">
                              <img
                                v-if="challenge.challenger_team_logo"
                                :src="challenge.challenger_team_logo"
                                :alt="challenge.challenger_team_name"
                                loading="lazy"
                                decoding="async"
                                @error="handleImageError"
                              />
                              <div v-else class="avatar-placeholder">
                                <i class="fa-solid fa-shield-halved" aria-hidden="true"></i>
                              </div>
                            </div>
                            <div class="team-details">
                              <div class="team-name">{{ challenge.challenger_team_name }}</div>
                              <div class="challenge-label">wants to challenge your team</div>
                            </div>
                          </div>
                          <div class="challenge-details">
                            <div v-if="challengeDate(challenge)" class="detail-item">
                              <i class="fa-solid fa-calendar-day" aria-hidden="true"></i>
                              <span>
                                {{ formatDate(challengeDate(challenge)) }}
                                at {{ formatTime(challengeDate(challenge)) }}
                              </span>
                            </div>
                            <div class="detail-item">
                              <i class="fa-solid fa-hourglass-half" aria-hidden="true"></i>
                              <span>{{ challengeDuration(challenge) }} minutes</span>
                            </div>
                            <div class="detail-item">
                              <i class="fa-solid fa-futbol" aria-hidden="true"></i>
                              <span>{{ formatChallengeMatchFormat(challenge) }}</span>
                            </div>
                            <div v-if="challenge.match_type" class="detail-item">
                              <i class="fa-solid fa-trophy" aria-hidden="true"></i>
                              <span>{{ formatChallengeMatchType(challenge.match_type) }}</span>
                            </div>
                            <div v-if="challengeVenueLabel(challenge)" class="detail-item detail-item--venue">
                              <i class="fa-solid fa-map-marker-alt" aria-hidden="true"></i>
                              <div class="venue-info">
                                <span class="venue-text">{{ challengeVenueLabel(challenge) }}</span>
                                <a
                                  v-if="challengeVenuePinUrl(challenge)"
                                  :href="challengeVenuePinUrl(challenge)"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="venue-location-link"
                                  @click.stop
                                >
                                  <i class="fa-solid fa-map-location-dot" aria-hidden="true"></i>
                                  View pinned location
                                </a>
                              </div>
                            </div>
                            <div v-if="challenge.description" class="detail-item">
                              <i class="fa-solid fa-align-left" aria-hidden="true"></i>
                              <span>{{ challenge.description }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="booking-actions challenge-actions">
                        <button
                          type="button"
                          class="btn-action btn-success"
                          :disabled="processingChallenge === challenge.id"
                          @click.stop="acceptChallenge(challenge.id)"
                        >
                          <i class="fa-solid fa-check" aria-hidden="true"></i>
                          <span v-if="processingChallenge !== challenge.id">Accept</span>
                          <span v-else>Processing...</span>
                        </button>
                        <button
                          type="button"
                          class="btn-action btn-danger"
                          :disabled="processingChallenge === challenge.id"
                          @click.stop="rejectChallenge(challenge.id)"
                        >
                          <i class="fa-solid fa-times" aria-hidden="true"></i>
                          <span v-if="processingChallenge !== challenge.id">Reject</span>
                          <span v-else>Processing...</span>
                        </button>
                        <button
                          type="button"
                          class="btn-action btn-secondary"
                          @click.stop="viewChallengeDetails(challenge.id)"
                        >
                          <i class="fa-solid fa-eye" aria-hidden="true"></i>
                          Details
                        </button>
                      </div>
                    </article>
                  </transition-group>
                </div>
                <div v-else class="empty-state challenges-empty">
                  <div class="empty-icon">
                    <i class="fa-solid fa-inbox" aria-hidden="true"></i>
                  </div>
                  <h3>No Challenge Requests</h3>
                  <p>You don't have any pending challenge requests at the moment.</p>
                </div>
              </div>
            </div>
          </section>

          <!-- Two Column Layout -->
          <div class="content-grid">
            <!-- Left Column -->
            <div class="column">
              <UpcomingMatches
                :matches="upcomingMatches"
                :team-id="currentTeam.team_id"
                :format-date="formatDate"
                :format-time="formatTime"
                :format-match-status="formatMatchStatus"
                @view-all="viewAllMatches"
                @view-match="viewMatch"
                @view-details="viewMatchDetails"
                @organize-match="organizeMatch"
                @view-tactics="viewTacticsAndFormations"
              />

              <MatchOfficialsSlots
                :matches="upcomingOfficiatedMatches"
                :team-id="currentTeam.team_id"
                :format-date="formatDate"
                :format-time="formatTime"
              />

              <TeamPreferences
                :team="currentTeam"
                :format-preferred-days="formatPreferredDays"
                :capitalize-text="capitalizeText"
                @edit="editPreferences"
              />
            </div>

            <!-- Right Column -->
            <div class="column">
              <TeamPlayers
                :players="teamMembers"
                :user-role="userRole"
                :format-date="formatDate"
                @invite="invitePlayers"
                @remove="removePlayer"
              />

              <RecentActivity
                :activities="recentActivity"
                :format-time-ago="formatTimeAgo"
              />
            </div>
          </div>
        </div>

        <!-- Matches Tab -->
        <div v-if="activeTab === 'matches'" class="tab-content">
          <div class="section-header">
            <div class="section-title">
              <i class="fa-solid fa-futbol"></i>
              <h2>All Matches</h2>
            </div>
            <div class="section-controls">
              <div class="search-box">
                <i class="fa-solid fa-search"></i>
                <input 
                  v-model="matchSearch" 
                  type="text" 
                  placeholder="Search matches..."
                  @input="filterMatches"
                >
                <button v-if="matchSearch" class="clear-search" @click="clearMatchSearch">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
              <button class="btn-action edit-btn" @click="organizeMatch">
                <i class="fa-solid fa-plus"></i>
                New Match
              </button>
            </div>
          </div>

          <!-- Filter Tabs -->
          <div class="filter-tabs">
            <button 
              v-for="filter in matchFilters" 
              :key="filter.id"
              class="tab-btn"
              :class="{ 'active': matchFilter === filter.id }"
              @click="matchFilter = filter.id"
            >
              <i :class="filter.icon"></i>
              <span>{{ filter.label }}</span>
              <span v-if="filter.count" class="tab-count">{{ filter.count }}</span>
            </button>
          </div>

          <!-- Matches Grid -->
          <div class="matches-grid booking-list-container">
            <transition-group name="booking-list">
              <div 
                v-for="match in filteredMatches" 
                :key="match.id"
                class="match-card booking-card"
                :class="match.status"
                @click="viewMatch(match.id)"
              >
                <div class="booking-header">
                  <div class="booking-id">
                    <i class="fa-solid fa-futbol"></i>
                    <span>Match #{{ match.id }}</span>
                  </div>
                  <div class="booking-meta">
                    <span class="booking-date">
                      <i class="fa-solid fa-clock"></i>
                      {{ formatDate(match.date) }}
                    </span>
                    <span class="booking-status" :class="match.status">
                      {{ formatMatchStatus(match.status) }}
                    </span>
                  </div>
                </div>
                <div class="booking-body">
                  <div class="match-teams">
                    <div class="team-info home-team">
                      <div class="team-name">{{ currentTeam.name }}</div>
                      <div class="team-score">{{ match.home_score || '-' }}</div>
                    </div>
                    <div class="vs-divider">VS</div>
                    <div class="team-info away-team">
                      <div class="team-name">{{ match.opponent_name || 'TBD' }}</div>
                      <div class="team-score">{{ match.away_score || '-' }}</div>
                    </div>
                  </div>
                  <div class="match-details">
                    <div class="venue-info">
                      <i class="fa-solid fa-map-marker-alt"></i>
                      <span>{{ match.venue?.name || 'Venue TBD' }}</span>
                    </div>
                    <div class="time-info">
                      <i class="fa-solid fa-clock"></i>
                      <span>{{ formatTime(match.date) }}</span>
                    </div>
                  </div>
                </div>
                <div class="booking-actions">
                  <button class="btn-action btn-secondary" @click.stop="viewMatchDetails(match.id)">
                    <i class="fa-solid fa-eye"></i>
                    Details
                  </button>
                  <button 
                    v-if="['confirmed', 'scheduled'].includes((match.status || '').toLowerCase())" 
                    class="btn-action btn-primary" 
                    @click.stop="viewTacticsAndFormations(match.id)"
                  >
                    <i class="fa-solid fa-chess-board"></i>
                    Tactics and Formations
                  </button>
                  <button 
                    v-if="isPastMatch(match) && isChallengerForMatch(match)" 
                    class="btn-action btn-primary" 
                    @click.stop="updateMatchStats(match.id)"
                  >
                    <i class="fa-solid fa-chart-line"></i>
                    Update Match Stats
                  </button>
                </div>
              </div>
            </transition-group>

            <!-- Empty State -->
            <div v-if="filteredMatches.length === 0" class="empty-state">
              <div class="empty-icon">
                <i class="fa-solid fa-futbol"></i>
              </div>
              <h3>No Matches Found</h3>
              <p>{{ matchSearch ? 'Try a different search' : 'Start by organizing your first match' }}</p>
              <button class="btn-refresh" @click="organizeMatch">
                <i class="fa-solid fa-plus"></i>
                Organize Match
              </button>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="filteredMatches.length > 0" class="pagination">
            <button class="pagination-btn" :disabled="matchPage === 1" @click="matchPage--">
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <div class="page-numbers">
              <button 
                v-for="page in totalMatchPages" 
                :key="page"
                class="page-btn"
                :class="{ active: matchPage === page }"
                @click="matchPage = page"
              >
                {{ page }}
              </button>
            </div>
            <button class="pagination-btn" :disabled="matchPage === totalMatchPages" @click="matchPage++">
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- Players Tab -->
        <div v-if="activeTab === 'players'" class="tab-content">
          <div class="section-header">
            <div class="section-title">
              <i class="fa-solid fa-users"></i>
              <h2>Team Players</h2>
            </div>
            <div class="section-controls">
              <div class="search-box">
                <i class="fa-solid fa-search"></i>
                <input 
                  v-model="playerSearch" 
                  type="text" 
                  placeholder="Search players..."
                  @input="filterPlayers"
                >
                <button v-if="playerSearch" class="clear-search" @click="clearPlayerSearch">
                  <i class="fa-solid fa-times"></i>
                </button>
              </div>
              <button v-if="isTeamOwner" class="btn-action edit-btn" @click="invitePlayers">
                <i class="fa-solid fa-user-plus"></i>
                Invite Players
              </button>
            </div>
          </div>

          <!-- Player Grid -->
          <div class="players-grid booking-list-container">
            <transition-group name="booking-list">
              <div 
                v-for="player in filteredPlayers" 
                :key="player.member_id"
                class="player-card booking-card"
              >
                <div class="booking-header">
                  <div class="booking-customer">
                    <div class="customer-avatar">
                      <i class="fa-solid fa-user"></i>
                    </div>
                    <div class="customer-info">
                      <h4>{{ player.name }}</h4>
                      <div class="customer-contact">
                        <span>{{ player.position || 'Player' }}</span>
                        <span v-if="player.email">
                          <i class="fa-solid fa-envelope"></i>
                          {{ player.email }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="booking-meta">
                    <span class="booking-status" :class="player.status">
                      {{ player.status }}
                    </span>
                  </div>
                </div>
                <div class="booking-body">
                  <div class="player-stats-grid">
                    <div class="stat">
                      <strong>{{ player.stats?.matches || 0 }}</strong>
                      <span>Matches</span>
                    </div>
                    <div class="stat">
                      <strong>{{ player.stats?.goals || 0 }}</strong>
                      <span>Goals</span>
                    </div>
                    <div class="stat">
                      <strong>{{ player.stats?.assists || 0 }}</strong>
                      <span>Assists</span>
                    </div>
                    <div class="stat">
                      <strong>{{ player.rating || 'N/A' }}</strong>
                      <span>Rating</span>
                    </div>
                  </div>
                  <div class="player-details">
                    <div class="detail-item">
                      <i class="fa-solid fa-user-tag"></i>
                      <span>{{ player.role }}</span>
                    </div>
                    <div v-if="player.is_captain" class="detail-item">
                      <i class="fa-solid fa-crown"></i>
                      <span>Captain</span>
                    </div>
                    <div class="detail-item">
                      <i class="fa-solid fa-calendar"></i>
                      <span>Joined {{ formatDate(player.joined_at) }}</span>
                    </div>
                  </div>
                </div>
                <div class="booking-actions">
                  <button v-if="userRole === 'owner'" class="btn-action btn-danger" @click.stop="removePlayer(player.member_id)">
                    <i class="fa-solid fa-user-minus"></i>
                    Remove
                  </button>
                </div>
              </div>
            </transition-group>

            <!-- Empty State -->
            <div v-if="filteredPlayers.length === 0" class="empty-state">
              <div class="empty-icon">
                <i class="fa-solid fa-users-slash"></i>
              </div>
              <h3>No Players Found</h3>
              <p>{{ playerSearch ? 'Try a different search' : 'Invite players to build your team' }}</p>
              <button v-if="isTeamOwner" class="btn-refresh" @click="invitePlayers">
                <i class="fa-solid fa-user-plus"></i>
                Invite Players
              </button>
            </div>
          </div>
        </div>

        <!-- Invites Tab -->
        <div v-if="activeTab === 'invites'" class="tab-content">
          <InvitePlayersPanel
            v-if="showInvitePanel && isTeamOwner && currentTeam.team_id"
            :team-id="currentTeam.team_id"
            sport="football"
            @close="showInvitePanel = false"
            @invites-sent="onInvitesSent"
          />

          <div class="section-header">
            <div class="section-title">
              <i class="fa-solid fa-envelope"></i>
              <h2>Team Invites</h2>
            </div>
            <div class="section-controls">
              <div class="filter-tabs">
                <button 
                  v-for="filter in inviteFilters" 
                  :key="filter.id"
                  class="tab-btn"
                  :class="{ 'active': inviteStatusFilter === filter.id }"
                  @click="inviteStatusFilter = filter.id"
                >
                  <i :class="filter.icon"></i>
                  <span>{{ filter.label }}</span>
                  <span v-if="filter.count !== undefined" class="tab-count">{{ filter.count }}</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Invites List -->
          <div class="invites-list booking-list-container">
            <transition-group name="booking-list">
              <div 
                v-for="invite in filteredInvites" 
                :key="invite.invite_id"
                class="invite-card booking-card"
                :class="invite.status"
              >
                <div class="booking-header">
                  <div class="booking-customer">
                    <div class="customer-avatar">
                      <i class="fa-solid fa-user"></i>
                    </div>
                    <div class="customer-info">
                      <h4>{{ invite.receiver_name || invite.receiver_email || 'Unknown' }}</h4>
                      <div class="customer-contact">
                        <span v-if="invite.receiver_email">
                          <i class="fa-solid fa-envelope"></i>
                          {{ invite.receiver_email }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="booking-meta">
                    <span class="booking-status" :class="invite.status">
                      {{ capitalizeStatus(invite.status) }}
                    </span>
                  </div>
                </div>
                <div class="booking-body">
                  <div class="booking-details">
                    <div class="time-details">
                      <div class="time-slot">
                        <i class="fa-solid fa-calendar"></i>
                        Sent {{ formatDate(invite.created_at) }}
                      </div>
                      <div class="duration">
                        <i class="fa-solid fa-clock"></i>
                        Expires {{ formatDate(invite.expires_at) }}
                      </div>
                    </div>
                    <div class="booking-amount">
                      <div class="amount-label">Invite Code</div>
                      <div class="amount-value">{{ invite.invite_code }}</div>
                    </div>
                  </div>
                  <!-- Player Stats (if invitee is already a player) -->
                  <div v-if="invite.player_stats" class="booking-notes">
                    <i class="fa-solid fa-chart-bar"></i>
                    <p>
                      <strong>Player Stats:</strong>
                      {{ invite.player_stats.goals || 0 }} goals • 
                      {{ invite.player_stats.assists || 0 }} assists • 
                      {{ invite.player_stats.clean_sheets || 0 }} clean sheets
                    </p>
                  </div>
                  <div v-else-if="invite.status === 'accepted'" class="booking-notes">
                    <i class="fa-solid fa-check-circle"></i>
                    <p>Invite accepted - Player is now a team member</p>
                  </div>
                  <div v-else-if="invite.status === 'pending'" class="booking-notes">
                    <i class="fa-solid fa-hourglass-half"></i>
                    <p>Waiting for response</p>
                  </div>
                  <div v-else-if="invite.status === 'declined'" class="booking-notes">
                    <i class="fa-solid fa-times-circle"></i>
                    <p>Invite was declined</p>
                  </div>
                  <div v-else-if="invite.status === 'expired'" class="booking-notes">
                    <i class="fa-solid fa-exclamation-triangle"></i>
                    <p>Invite has expired</p>
                  </div>
                </div>
                <div class="booking-actions">
                  <button 
                    v-if="invite.status === 'pending'" 
                    class="btn-action btn-secondary" 
                    @click.stop="cancelInvite(invite.invite_id)"
                  >
                    <i class="fa-solid fa-times"></i>
                    Cancel
                  </button>
                </div>
              </div>
            </transition-group>

            <!-- Empty State -->
            <div v-if="filteredInvites.length === 0" class="empty-state">
              <div class="empty-icon">
                <i class="fa-solid fa-envelope-open"></i>
              </div>
              <h3>No Invites Found</h3>
              <p>{{ inviteStatusFilter !== 'all' ? 'No invites with this status' : 'No invites have been sent yet' }}</p>
              <button class="btn-refresh" @click="invitePlayers">
                <i class="fa-solid fa-user-plus"></i>
                Send Invites
              </button>
            </div>
          </div>
        </div>

        <!-- Stats Tab -->
        <div v-if="activeTab === 'stats'" class="tab-content">
          <div class="section-header">
            <div class="section-title">
              <i class="fa-solid fa-chart-bar"></i>
              <h2>Team Statistics</h2>
            </div>
          </div>

          <!-- Quick Stats -->
          <div class="quick-stats">
            <div class="stat-item">
              <i class="fa-solid fa-trophy"></i>
              <div>
                <span class="stat-value">{{ currentTeam.wins || 0 }}</span>
                <span class="stat-label">Wins</span>
              </div>
            </div>
            <div class="stat-item">
              <i class="fa-solid fa-times"></i>
              <div>
                <span class="stat-value">{{ currentTeam.losses || 0 }}</span>
                <span class="stat-label">Losses</span>
              </div>
            </div>
            <div class="stat-item">
              <i class="fa-solid fa-equals"></i>
              <div>
                <span class="stat-value">{{ currentTeam.draws || 0 }}</span>
                <span class="stat-label">Draws</span>
              </div>
            </div>
            <div class="stat-item">
              <i class="fa-solid fa-futbol"></i>
              <div>
                <span class="stat-value">{{ teamGoals }}</span>
                <span class="stat-label">Goals</span>
              </div>
            </div>
          </div>

          <!-- Performance Overview -->
          <div class="stats-overview">
            <div class="stat-big-card">
              <h3>Performance Overview</h3>
              <div class="stats-row">
                <div class="stat-item">
                  <strong>{{ winRate }}%</strong>
                  <span>Win Rate</span>
                </div>
                <div class="stat-item">
                  <strong>{{ avgGoalsPerMatch }}</strong>
                  <span>Avg Goals/Match</span>
                </div>
                <div class="stat-item">
                  <strong>{{ cleanSheets }}</strong>
                  <span>Clean Sheets</span>
                </div>
                <div class="stat-item">
                  <strong>{{ longestWinStreak }}</strong>
                  <span>Longest Win Streak</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Player Stats Table -->
          <div class="card">
            <div class="card-header">
              <div class="card-title">
                <i class="fa-solid fa-user-chart"></i>
                <h2>Player Statistics</h2>
              </div>
            </div>
            <div class="card-body">
              <div class="table-responsive">
                <table class="stats-table">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Position</th>
                      <th>Matches</th>
                      <th>Goals</th>
                      <th>Assists</th>
                      <th>Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="player in teamMembers" :key="player.member_id">
                      <td class="player-cell">
                        <div class="player-avatar-sm">
                          <i class="fa-solid fa-user"></i>
                        </div>
                        <span>{{ player.name }}</span>
                      </td>
                      <td>{{ player.position || '-' }}</td>
                      <td>{{ player.stats?.matches || 0 }}</td>
                      <td>{{ player.stats?.goals || 0 }}</td>
                      <td>{{ player.stats?.assists || 0 }}</td>
                      <td>
                        <span class="rating-badge">
                          {{ player.rating || 'N/A' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <ThirdpartyFooter />

    <!-- Confirmation Modal -->
    <DeleteConfirmModal
      :show="showDeleteConfirm"
      :team="currentTeam"
      @cancel="cancelDelete"
      @confirm="deleteTeam"
    />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ToastContainer from "@/components/ui/ToastContainer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import toastMixin from "@/mixins/toastMixin";
import TeamHeader from "@/components/teamdashboard/TeamHeader.vue";
import StatsCards from "@/components/teamdashboard/StatsCards.vue";
import UpcomingMatches from "@/components/teamdashboard/UpcomingMatches.vue";
import MatchOfficialsSlots from "@/components/teamdashboard/MatchOfficialsSlots.vue";
import TeamPreferences from "@/components/teamdashboard/TeamPreferences.vue";
import TeamPlayers from "@/components/teamdashboard/TeamPlayers.vue";
import RecentActivity from "@/components/teamdashboard/RecentActivity.vue";
import DeleteConfirmModal from "@/components/teamdashboard/DeleteConfirmModal.vue";
import InvitePlayersPanel from "@/components/teamdashboard/InvitePlayersPanel.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import { buildApiUrl } from "@/config/api";
import { useAuthStore } from "@/authen/auth";
import { verifyMatchHubTeamOwnerAccess } from "@/utils/matchHubAccess";

const OWNER_TEAM_ACTION_DENIED_MESSAGE = "Sorry unable to complete request";

export default {
  name: 'TeamDashboard',
  loadingMessage: 'Loading team data...',
  mixins: [toastMixin, globalLoadingMixin],
  components: {
    MainNavbar,
    ToastContainer,
    TeamHeader,
    StatsCards,
    UpcomingMatches,
    MatchOfficialsSlots,
    TeamPreferences,
    TeamPlayers,
    RecentActivity,
    DeleteConfirmModal,
    InvitePlayersPanel,
    ThirdpartyFooter
  },
  
  data() {
    return {
      skipDashboardInit: false,
      teamAccessPromise: null,
      loading: false,
      activeTab: 'overview',
      currentTeam: {
        team_id: null,
        name: '',
        team_type: '',
        bio: '',
        logo_url: null,
        phone: '',
        home_ground: '',
        home_ground_pin_location: '',
        county: '',
        constituency: '',
        skill_level: '',
        availability: '',
        preferences: {},
        is_active: true,
        is_verified: false,
        total_matches: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        member_count: 0,
        owner_id: null,
        owner_name: '',
        created_at: null,
        updated_at: null
      },
      teamMembers: [],
      teamMatches: [],
      upcomingMatches: [],
      challenges: [],
      loadingChallenges: false,
      processingChallenge: null,
      recentActivity: [],
      teamInvites: [],
      inviteStatusFilter: 'all',
      
      tabs: [
        { id: 'overview', label: 'Overview', icon: 'fa-solid fa-home' },
        { id: 'matches', label: 'Matches', icon: 'fa-solid fa-futbol' },
        { id: 'players', label: 'Players', icon: 'fa-solid fa-users' },
        { id: 'invites', label: 'Invites', icon: 'fa-solid fa-envelope', count: 0 },
        { id: 'stats', label: 'Stats', icon: 'fa-solid fa-chart-bar' }
      ],
      
      matchSearch: '',
      playerSearch: '',
      matchFilter: 'all',
      
      matchPage: 1,
      matchesPerPage: 6,
      showDeleteConfirm: false,
      showInvitePanel: false,
      userRole: 'owner' // owner, captain, member
    };
  },
  
  computed: {
    isTeamOwner() {
      return this.userRole === "owner";
    },

    activePlayersCount() {
      return this.teamMembers.filter(m => m.status === 'active').length;
    },
    
    winRate() {
      const total = this.currentTeam.wins + this.currentTeam.losses + this.currentTeam.draws;
      return total > 0 ? Math.round((this.currentTeam.wins / total) * 100) : 0;
    },
    
    winTrend() {
      // This would come from backend analytics
      return 'positive';
    },
    
    winTrendIcon() {
      return this.winTrend === 'positive' ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down';
    },
    
    winTrendText() {
      return this.winTrend === 'positive' ? '+5% from last month' : '-2% from last month';
    },
    
    upcomingMatchRate() {
      // Calculate percentage increase in matches this month
      return 25; // Sample data
    },

    upcomingOfficiatedMatches() {
      return this.upcomingMatches
        .filter((match) => match.needs_officiating && Number(match.officiating_count) > 0)
        .sort((a, b) => {
          const da = new Date(a.date || a.scheduled_at || 0).getTime();
          const db = new Date(b.date || b.scheduled_at || 0).getTime();
          return da - db;
        });
    },
    
    newPlayersThisMonth() {
      return this.teamMembers.filter(m => {
        const joinDate = new Date(m.joined_at);
        const now = new Date();
        return joinDate.getMonth() === now.getMonth() && joinDate.getFullYear() === now.getFullYear();
      }).length;
    },
    
    teamRating() {
      const ratings = this.teamMembers
        .filter(m => m.rating)
        .map(m => m.rating);
      return ratings.length > 0 
        ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1)
        : 'N/A';
    },
    
    ratingCount() {
      return this.teamMembers.filter(m => m.rating).length;
    },
    
    teamGoals() {
      return this.teamMembers.reduce((total, player) => total + (player.stats?.goals || 0), 0);
    },
    
    avgGoalsPerMatch() {
      const totalMatches = this.currentTeam.total_matches || 1;
      return (this.teamGoals / totalMatches).toFixed(1);
    },
    
    cleanSheets() {
      // This would come from backend
      return 4; // Sample data
    },
    
    longestWinStreak() {
      // This would come from backend
      return 3; // Sample data
    },
    
    filteredMatches() {
      let matches = this.teamMatches;
      
      // Apply search filter
      if (this.matchSearch) {
        const search = this.matchSearch.toLowerCase();
        matches = matches.filter(match => 
          match.opponent_name?.toLowerCase().includes(search) ||
          match.venue?.name?.toLowerCase().includes(search) ||
          match.status?.toLowerCase().includes(search)
        );
      }
      
      // Apply status filter
      if (this.matchFilter !== 'all') {
        matches = matches.filter((match) => {
          if (this.matchFilter === "upcoming") {
            return !this.isPastMatch(match);
          }
          if (this.matchFilter === "past") {
            return this.isPastMatch(match);
          }
          return (match.status || "").toLowerCase() === this.matchFilter;
        });
      }

      // Apply pagination
      const start = (this.matchPage - 1) * this.matchesPerPage;
      const end = start + this.matchesPerPage;
      return matches.slice(start, end);
    },
    
    totalMatchPages() {
      let matches = this.teamMatches;
      
      if (this.matchSearch) {
        const search = this.matchSearch.toLowerCase();
        matches = matches.filter(match => 
          match.opponent_name?.toLowerCase().includes(search) ||
          match.venue?.name?.toLowerCase().includes(search) ||
          match.status?.toLowerCase().includes(search)
        );
      }
      
      if (this.matchFilter !== "all") {
        matches = matches.filter((match) => {
          if (this.matchFilter === "upcoming") {
            return !this.isPastMatch(match);
          }
          if (this.matchFilter === "past") {
            return this.isPastMatch(match);
          }
          return (match.status || "").toLowerCase() === this.matchFilter;
        });
      }

      return Math.ceil(matches.length / this.matchesPerPage);
    },
    
    filteredPlayers() {
      let players = this.teamMembers;
      
      if (this.playerSearch) {
        const search = this.playerSearch.toLowerCase();
        players = players.filter(player => 
          player.name?.toLowerCase().includes(search) ||
          player.position?.toLowerCase().includes(search) ||
          player.email?.toLowerCase().includes(search)
        );
      }
      
      return players;
    },
    
    // Update filter counts
    matchFilters() {
      return [
        { 
          id: 'all', 
          label: 'All Matches', 
          icon: 'fa-solid fa-list',
          count: this.teamMatches.length
        },
        { 
          id: 'upcoming', 
          label: 'Upcoming', 
          icon: 'fa-solid fa-calendar-plus',
          count: this.teamMatches.filter((m) => !this.isPastMatch(m)).length
        },
        { 
          id: 'past', 
          label: 'Past', 
          icon: 'fa-solid fa-history',
          count: this.teamMatches.filter((m) => this.isPastMatch(m)).length
        },
        { 
          id: 'pending', 
          label: 'Pending', 
          icon: 'fa-solid fa-clock',
          count: this.teamMatches.filter(m => m.status === 'pending').length
        }
      ];
    },
    
    filteredInvites() {
      let invites = this.teamInvites;
      
      if (this.inviteStatusFilter !== 'all') {
        invites = invites.filter(invite => invite.status === this.inviteStatusFilter);
      }
      
      return invites;
    },
    
    inviteFilters() {
      return [
        { 
          id: 'all', 
          label: 'All', 
          icon: 'fa-solid fa-list',
          count: this.teamInvites.length
        },
        { 
          id: 'pending', 
          label: 'Pending', 
          icon: 'fa-solid fa-clock',
          count: this.teamInvites.filter(i => i.status === 'pending').length
        },
        { 
          id: 'accepted', 
          label: 'Accepted', 
          icon: 'fa-solid fa-check-circle',
          count: this.teamInvites.filter(i => i.status === 'accepted').length
        },
        { 
          id: 'declined', 
          label: 'Declined', 
          icon: 'fa-solid fa-times-circle',
          count: this.teamInvites.filter(i => i.status === 'declined').length
        },
        { 
          id: 'expired', 
          label: 'Expired', 
          icon: 'fa-solid fa-exclamation-triangle',
          count: this.teamInvites.filter(i => i.status === 'expired').length
        }
      ];
    }
  },
  
  beforeMount() {
    const auth = useAuthStore();
    auth.checkToken();
    if (!auth.isAuthenticated) {
      this.skipDashboardInit = true;
      this.$router.replace({
        name: "CustomerSignIn",
        query: { redirect: this.$route.fullPath },
      });
      return;
    }
    this.teamAccessPromise = verifyMatchHubTeamOwnerAccess();
  },

  async mounted() {
    if (this.skipDashboardInit) return;
    const result = await this.teamAccessPromise;
    if (!result?.ok) {
      if (result?.reason === "session" || result?.reason === "unauthenticated") {
        this.$router.replace({
          name: "CustomerSignIn",
          query: { redirect: this.$route.fullPath },
        });
      } else {
        this.$router.replace({ name: "MatchesHub" });
      }
      return;
    }
    if (!result.has_customer || !result.has_team) {
      this.$router.replace({ name: "FootballTeamRegistration" });
      return;
    }
    await this.loadTeamData();
    this.applyRouteTabQuery();
  },
  
  methods: {
  capitalizeWord(word) {
  if (!word) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
},

  capitalizeText(text) {
    if (!text || typeof text !== 'string') return text;
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  },

  formatPreferredDays(days) {
    if (!days || !Array.isArray(days) || days.length === 0) {
      return [];
    }
    
    const dayMap = {
      'monday': 'Monday',
      'tuesday': 'Tuesday',
      'wednesday': 'Wednesday',
      'thursday': 'Thursday',
      'friday': 'Friday',
      'saturday': 'Saturday',
      'sunday': 'Sunday',
      'mon': 'Monday',
      'tue': 'Tuesday',
      'wed': 'Wednesday',
      'thu': 'Thursday',
      'fri': 'Friday',
      'sat': 'Saturday',
      'sun': 'Sunday'
    };
    
    return days.map(day => {
      const dayLower = day.toLowerCase().trim();
      return dayMap[dayLower] || this.capitalizeText(day);
    });
  },
    getAuthToken() {
      return (
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token')
      );
    },

    showOwnerActionDeniedToast() {
      this.showToast("error", "", OWNER_TEAM_ACTION_DENIED_MESSAGE);
    },

    normalizeTeamMembersForDashboard(members) {
      return members.map((m, index) => {
        const memberId = m.team_member_number ?? m.member_id;
        return {
          ...m,
          member_id: memberId,
          name: m.name || m.player_name || `Member ${index + 1}`,
          email: m.email || m.player_email || "",
          joined_at: m.joined_at || null,
        };
      });
    },

    resolveTeamFromPayload(payload) {
      // Backend `/api/teams/my-teams` returns { message, teams: [...] }
      // Backend `/api/teams/{id}` returns a single team object
      if (!payload) return null;

      const routeTeamId =
        this.$route?.params?.team_id ||
        this.$route?.params?.id ||
        this.$route?.query?.team_id ||
        this.$route?.query?.teamId ||
        null;

      if (Array.isArray(payload.teams)) {
        if (payload.teams.length === 0) return null;
        if (routeTeamId) {
          const match = payload.teams.find(
            (t) => String(t?.team_id) === String(routeTeamId)
          );
          return match || payload.teams[0];
        }
        return payload.teams[0];
      }

      if (payload.team && typeof payload.team === 'object') return payload.team;
      if (payload.team_id || payload.team_id_num) return payload;

      return null;
    },

    mapTeamToViewModel(teamData) {
      if (!teamData) return null;
      return {
        team_id: teamData.team_id ?? teamData.team_id_num ?? null,
        name: teamData.name ?? teamData.team_name ?? '',
        team_type: teamData.team_type ?? '',
        bio: teamData.bio || '',
        logo_url: teamData.logo_url || null,
        phone: teamData.phone || '',
        home_ground: teamData.home_ground || '',
        home_ground_pin_location: teamData.home_ground_pin_location || '',
        county: teamData.county || '',
        constituency: teamData.constituency || '',
        skill_level: this.capitalizeWord(teamData.skill_level) || 'beginner',
        availability: teamData.availability || 'weekends',
        preferences: teamData.preferences || {},
        is_active: teamData.is_active ?? true,
        is_verified: teamData.is_verified || false,
        total_matches: teamData.total_matches || 0,
        wins: teamData.wins || 0,
        losses: teamData.losses || 0,
        draws: (teamData.total_matches || 0) - (teamData.wins || 0) - (teamData.losses || 0), // Calculate draws
        member_count: teamData.member_count || 0,
        owner_id: teamData.owner_id || null,
        owner_name: teamData.owner_name || '',
        created_at: teamData.created_at || null,
        updated_at: teamData.updated_at || null
      };
    },

    async loadTeamData() {
      this.loading = true;
      try {
        const token = this.getAuthToken();
        if (!token) {
          throw new Error('No authentication token found');
        }

        // Fetch user's teams from the API
        const response = await fetch(buildApiUrl('/api/teams/my-teams?include_members=true'), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (!response.ok) {
          const errorData = await response.json();
          // If 404, user doesn't have a team yet
          if (response.status === 404) {
            this.showToast('info', 'No Team Found', 'You haven\'t created a team yet. Create one to get started!');
            return;
          }
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }

        const payload = await response.json();
        const selectedTeam = this.resolveTeamFromPayload(payload);

        if (!selectedTeam) {
          this.showToast('info', 'No Team Found', 'You haven\'t created a team yet. Create one to get started!');
          return;
        }

        // Map API response to component data structure
        const mapped = this.mapTeamToViewModel(selectedTeam);
        if (!mapped?.team_id) {
          throw new Error('Team data loaded, but team_id is missing.');
        }
        this.currentTeam = mapped;

        // `/api/teams/my-teams` returns only a team the user owns — always treat as owner for edits
        this.userRole = "owner";
        this.teamMembers = this.normalizeTeamMembersForDashboard(selectedTeam.members || []);
        
        // Load team matches (you'll need to implement this endpoint)
        await this.loadTeamMatches();
        // Load challenge requests
        await this.loadChallenges();
        
        // Set recent activity (you'll need to implement this endpoint)
        await this.loadRecentActivity();
        
        // Load team invites
        await this.loadTeamInvites();
        
        this.showToast('success', 'Team loaded successfully', 'Your team dashboard is ready');
        
      } catch (error) {
        console.error('Error loading team data:', error);
        this.showToast('error', 'Failed to load team', error.message || 'Please try again');
      } finally {
        this.loading = false;
      }
    },
    
    async loadTeamMatches() {
      try {
        if (!this.currentTeam.team_id) return;
        
        const token = localStorage.getItem('access_token');
        const response = await fetch(buildApiUrl(`/api/matches/teams/${this.currentTeam.team_id}/matches`), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.teamMatches = data.matches || [];
          this.upcomingMatches = this.teamMatches.filter(
            (match) => !this.isPastMatch(match)
          );
        } else {
          console.warn('Matches endpoint not available');
          this.teamMatches = [];
          this.upcomingMatches = [];
        }
      } catch (error) {
        console.error('Error loading matches:', error);
        this.teamMatches = [];
        this.upcomingMatches = [];
      }
    },
    
    async loadChallenges() {
      this.loadingChallenges = true;
      try {
        if (!this.currentTeam.team_id) return;
        
        const token = this.getAuthToken();
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        const response = await fetch(buildApiUrl(`/api/matches/teams/${this.currentTeam.team_id}/challenges`), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.challenges = (data.challenges || []).map((c) => this.normalizeChallenge(c));
        } else {
          console.warn('Challenges endpoint not available');
          this.challenges = [];
        }
      } catch (error) {
        console.error('Error loading challenges:', error);
        this.challenges = [];
      } finally {
        this.loadingChallenges = false;
      }
    },
    
    normalizeChallenge(raw) {
      if (!raw || typeof raw !== 'object') return raw;
      const matchDate = raw.match_date || raw.scheduled_at || raw.date || null;
      const pin = raw.pin_location || raw.venue?.notes || null;
      const venue =
        raw.venue && typeof raw.venue === 'object'
          ? { ...raw.venue, notes: raw.venue.notes || pin }
          : {
              name: raw.venue_name || null,
              city: raw.venue_city || null,
              address: raw.venue_address || null,
              notes: pin,
            };
      return {
        ...raw,
        id: raw.id ?? raw.match_id,
        match_id: raw.match_id ?? raw.id,
        match_date: matchDate,
        scheduled_at: raw.scheduled_at || matchDate,
        duration: raw.duration_minutes ?? raw.duration,
        duration_minutes: raw.duration_minutes ?? raw.duration,
        challenger_team_name:
          raw.challenger_team_name || raw.challenger_name || raw.opponent_name || 'Unknown team',
        challenger_team_logo: raw.challenger_team_logo || raw.challenger_logo || null,
        venue,
        pin_location: pin,
      };
    },

    challengeDate(challenge) {
      return challenge?.match_date || challenge?.scheduled_at || challenge?.date || null;
    },

    challengeDuration(challenge) {
      const d = challenge?.duration_minutes ?? challenge?.duration;
      return Number.isFinite(d) && d > 0 ? d : 90;
    },

    challengeVenueLabel(challenge) {
      const v = challenge?.venue;
      const name = v?.name || challenge?.venue_name;
      if (!name) return '';
      const city = v?.city || challenge?.venue_city;
      return city ? `${name}, ${city}` : name;
    },

    challengeVenuePinUrl(challenge) {
      const pin =
        challenge?.venue?.notes ||
        challenge?.pin_location ||
        challenge?.venue?.pin_location ||
        null;
      if (!pin || typeof pin !== 'string') return null;
      const trimmed = pin.trim();
      if (!trimmed) return null;
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
    },

    formatChallengeMatchFormat(challenge) {
      const format = challenge?.match_format;
      if (!format) return 'Format TBD';
      const labels = {
        '11v11': '11v11',
        '9v9': '9v9',
        '7v7': '7v7',
        '5v5': '5v5',
        futsal: 'Futsal',
      };
      return labels[format] || String(format).replace(/_/g, ' ');
    },

    formatChallengeMatchType(type) {
      if (!type) return '';
      return String(type).charAt(0).toUpperCase() + String(type).slice(1);
    },

    async refreshChallenges() {
      await this.loadChallenges();
      this.showToast('success', 'Refreshed', 'Challenge requests updated');
    },
    
    async acceptChallenge(challengeId) {
      this.processingChallenge = challengeId;
      try {
        const token = this.getAuthToken();
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        const response = await fetch(buildApiUrl(`/api/matches/${challengeId}/accept`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          this.showToast('success', 'Challenge Accepted', 'The match has been confirmed!');
          // Remove from challenges list
          this.challenges = this.challenges.filter(c => c.id !== challengeId);
          // Reload matches to show the new confirmed match
          await this.loadTeamMatches();
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to accept challenge');
        }
      } catch (error) {
        console.error('Error accepting challenge:', error);
        this.showToast('error', 'Error', error.message || 'Failed to accept challenge');
      } finally {
        this.processingChallenge = null;
      }
    },
    
    async rejectChallenge(challengeId) {
      this.processingChallenge = challengeId;
      try {
        const token = this.getAuthToken();
        if (!token) {
          throw new Error('No authentication token found');
        }
        
        const response = await fetch(buildApiUrl(`/api/matches/${challengeId}/reject`), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          this.showToast('success', 'Challenge Rejected', 'The challenge request has been declined.');
          // Remove from challenges list
          this.challenges = this.challenges.filter(c => c.id !== challengeId);
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to reject challenge');
        }
      } catch (error) {
        console.error('Error rejecting challenge:', error);
        this.showToast('error', 'Error', error.message || 'Failed to reject challenge');
      } finally {
        this.processingChallenge = null;
      }
    },
    
    viewChallengeDetails(challengeId) {
      if (!challengeId || !this.currentTeam?.team_id) return;
      this.$router.push({
        name: "MatchDetails",
        params: { match_id: String(challengeId) },
        query: { team_id: String(this.currentTeam.team_id) },
      }).catch(() => {});
    },
    
    async loadRecentActivity() {
      try {
        if (!this.currentTeam.team_id) return;
        
        const token = localStorage.getItem('access_token');
        const response = await fetch(`/api/teams/${this.currentTeam.team_id}/activity`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          this.recentActivity = data.activity || [];
        } else {
          console.warn('Activity endpoint not available');
          this.recentActivity = [];
        }
      } catch (error) {
        console.error('Error loading activity:', error);
        this.recentActivity = [];
      }
    },
    
    async loadTeamInvites() {
      try {
        if (!this.currentTeam.team_id) return;
        
        const token = this.getAuthToken();
        if (!token) return;

        const response = await fetch(
          buildApiUrl(`/api/teams/${this.currentTeam.team_id}/invites`),
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          this.teamInvites = data.invites || [];
          
          const inviteTab = this.tabs.find(t => t.id === 'invites');
          if (inviteTab) {
            inviteTab.count = this.teamInvites.filter(i => i.status === 'pending').length;
          }
        } else {
          console.warn('Invites endpoint not available');
          this.teamInvites = [];
        }
      } catch (error) {
        console.warn('Error loading invites:', error);
        this.teamInvites = [];
      }
    },

    applyRouteTabQuery() {
      const tab = this.$route.query?.tab;
      if (tab && this.tabs.some((t) => t.id === tab)) {
        this.activeTab = tab;
      }
      if (this.$route.query?.compose === '1' && this.isTeamOwner) {
        this.activeTab = 'invites';
        this.showInvitePanel = true;
      }
    },

    onInvitesSent() {
      this.showInvitePanel = false;
      this.loadTeamInvites();
    },
    
    
    capitalizeStatus(status) {
      if (!status) return 'Unknown';
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    
    formatMatchStatus(status) {
      if (!status) return 'Unknown';
      const statusMap = {
        'pending': 'Awaiting opponent to confirm',
        'confirmed': 'Confirmed',
        'scheduled': 'Scheduled',
        'past': 'Past',
        'completed': 'Completed',
        'cancelled': 'Cancelled',
        'postponed': 'Postponed'
      };
      return statusMap[status.toLowerCase()] || this.capitalizeStatus(status);
    },
    
    cancelInvite() {
      // TODO: Implement cancel invite API call
      this.showToast('info', 'Cancel Invite', 'This feature will be implemented soon');
    },
    
    
    determineUserRole(ownerData) {
      // Get current user email from token or localStorage
      const userEmail = localStorage.getItem('user_email') || '';
      
      // Check if user is owner
      if (ownerData && ownerData.email === userEmail) {
        this.userRole = 'owner';
        return;
      }
      
      // Check if user is captain
      const captain = this.teamMembers.find(m => m.is_captain);
      if (captain && captain.email === userEmail) {
        this.userRole = 'captain';
        return;
      }
      
      // Check if user is member
      const member = this.teamMembers.find(m => m.email === userEmail);
      if (member) {
        this.userRole = 'member';
        return;
      }
      
      // Default to owner for demo purposes
      this.userRole = 'owner';
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    },
    
    formatTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    },
    
    formatTimeAgo(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) {
        return `${diffMins} ${diffMins === 1 ? 'minute' : 'minutes'} ago`;
      } else if (diffHours < 24) {
        return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
      } else if (diffDays < 7) {
        return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
      } else {
        return this.formatDate(dateString);
      }
    },
    
    handleImageError(event) {
      if (!event || !event.target) return;
      
      // Hide the broken image
      event.target.style.display = 'none';
      
      // Find and show the placeholder - check for different placeholder classes
      const parent = event.target.parentElement;
      if (!parent) return;
      
      // Try different placeholder class names
      const placeholder = parent.querySelector('.team-logo-placeholder') || 
                         parent.querySelector('.avatar-placeholder') ||
                         parent.querySelector('.logo-placeholder');
      
      if (placeholder) {
        placeholder.style.display = 'flex';
      }
    },
    

    // Navigation methods
    organizeMatch() {
      if (!this.currentTeam.team_id) {
        this.showToast('error', 'No Team', 'Please create a team first');
        return;
      }
      this.$router.push({ name: "FootballOrganizeMatch" }).catch(() => {});
    },
    
    openMatchDetailsPage(matchId) {
      if (!matchId || !this.currentTeam?.team_id) return;
      this.$router.push({
        name: "MatchDetails",
        params: { match_id: String(matchId) },
        query: { team_id: String(this.currentTeam.team_id) },
      }).catch(() => {});
    },

    viewMatch(matchId) {
      this.openMatchDetailsPage(matchId);
    },

    viewMatchDetails(matchId) {
      this.openMatchDetailsPage(matchId);
    },

    matchEndDate(match) {
      if (!match) return null;
      const start = new Date(match.date || match.scheduled_at);
      if (Number.isNaN(start.getTime())) return null;
      const mins = parseInt(match.duration ?? match.duration_minutes ?? 90, 10) || 90;
      return new Date(start.getTime() + mins * 60000);
    },

    isPastMatch(match) {
      if (!match) return false;
      const status = (match.status || "").toLowerCase();
      if (["completed", "cancelled", "past"].includes(status)) return true;
      const end = this.matchEndDate(match);
      if (end && end < new Date()) return true;
      return false;
    },

    isChallengerForMatch(match) {
      if (!match || !this.currentTeam || this.currentTeam.team_id == null) return false;
      const homeTeamId = match.home_team_id ?? match.team_id;
      return Number(homeTeamId) === Number(this.currentTeam.team_id);
    },

    updateMatchStats(matchId) {
      if (!this.currentTeam || !this.currentTeam.team_id) return;
      this.$router.push(`/ipromiseafrica/matchup/stats/${this.currentTeam.team_id}/match/${matchId}`);
    },
    
    viewTacticsAndFormations(matchId) {
      if (!this.currentTeam.team_id) {
        this.showToast('error', 'No Team', 'Please create a team first');
        return;
      }
      this.$router.push(`/ipromiseafrica/matchup/tactics/${this.currentTeam.team_id}/match/${matchId}`);
    },
    
    async removePlayer(memberId) {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      const player = this.teamMembers.find(m => m.member_id === memberId);
      if (!player) return;
      if (String(player.role || "").toLowerCase() === "owner") {
        this.showToast("error", "Not allowed", "You cannot remove the team owner.");
        return;
      }
      if (!confirm(`Are you sure you want to remove ${player.name} from the team?`)) return;

      const token = this.getAuthToken();
      if (!token) {
        this.showToast("error", "Sign in required", "Please log in again.");
        this.$router.push({ name: "CustomerSignIn", query: { redirect: this.$route.fullPath } });
        return;
      }

      try {
        const response = await fetch(
          buildApiUrl(`/api/teams/member/delete/${memberId}`),
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const errorData = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 403) {
            this.showOwnerActionDeniedToast();
            return;
          }
          const detail = errorData.detail;
          const msg =
            typeof detail === "string"
              ? detail
              : Array.isArray(detail)
                ? detail.map((e) => e.msg || e).join("; ")
                : "Failed to remove player";
          throw new Error(msg);
        }

        this.teamMembers = this.teamMembers.filter((m) => m.member_id !== memberId);
        if (this.currentTeam.member_count > 0) {
          this.currentTeam.member_count -= 1;
        }
        this.showToast("success", "Player Removed", `${player.name} has been removed from the team`);
      } catch (error) {
        console.error("Error removing player:", error);
        this.showToast("error", "Remove failed", error.message || "Failed to remove player");
      }
    },
    
    invitePlayers() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      if (!this.currentTeam.team_id) {
        this.showToast('error', 'Team ID not found. Please try again.');
        return;
      }
      this.activeTab = "invites";
      this.showInvitePanel = true;
    },
    
    editTeam() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      if (!this.currentTeam.name) {
        this.showToast('error', 'No Team', 'Please create a team first');
        return;
      }
      this.$router.push(`/ipromiseafrica/matchup/team-edit/${this.currentTeam.name}/edit`);
    },
    
    editPreferences() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      if (!this.currentTeam.team_id) {
        this.showToast('error', 'No Team', 'Please create a team first');
        return;
      }
      this.$router.push({
        name: "TeamEdit",
        params: { s: String(this.currentTeam.team_id) },
      });
    },
    
    viewAllMatches() {
      this.activeTab = 'matches';
    },
    
    filterMatches() {
      this.matchPage = 1;
    },
    
    clearMatchSearch() {
      this.matchSearch = '';
      this.filterMatches();
    },
    
    filterPlayers() {
      // Player filtering logic
    },
    
    clearPlayerSearch() {
      this.playerSearch = '';
      this.filterPlayers();
    },
    
    confirmDeleteTeam() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        return;
      }
      if (!this.currentTeam?.team_id) {
        this.showToast("error", "No team", "There is no team to delete.");
        return;
      }
      this.showDeleteConfirm = true;
    },
    
    cancelDelete() {
      this.showDeleteConfirm = false;
    },
    
    async deleteTeam() {
      if (!this.isTeamOwner) {
        this.showOwnerActionDeniedToast();
        this.showDeleteConfirm = false;
        return;
      }
      const teamId = this.currentTeam?.team_id;
      if (!teamId) {
        this.showToast("error", "No team", "Missing team.");
        this.showDeleteConfirm = false;
        return;
      }

      const token = this.getAuthToken();
      if (!token) {
        this.showToast("error", "Sign in required", "Please log in again.");
        this.showDeleteConfirm = false;
        this.$router.push({ name: "CustomerSignIn", query: { redirect: this.$route.fullPath } });
        return;
      }

      try {
        const response = await fetch(buildApiUrl(`/api/teams/${teamId}/delete`), {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const errorData = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 403) {
            this.showOwnerActionDeniedToast();
            this.showDeleteConfirm = false;
            return;
          }
          const detail = errorData.detail;
          const msg =
            typeof detail === "string"
              ? detail
              : Array.isArray(detail)
                ? detail.map((e) => e.msg || e).join("; ")
                : "Failed to delete team";
          throw new Error(msg);
        }

        const teamName = this.currentTeam.name || "Your team";
        this.showDeleteConfirm = false;
        this.showToast("success", "Team deleted", `${teamName} has been removed. Returning to Match Hub.`);
        this.$router.push({ name: "MatchesHub" });
      } catch (error) {
        this.showToast("error", "Deletion failed", error.message || "Failed to delete team");
        this.showDeleteConfirm = false;
      }
    }
  }
};
</script>

<style scoped>
/* === GLOBAL STYLES === */
.team-dashboard {
  background: var(--dashboard-bg);
  min-height: 100vh;
  position: relative;
  padding-top: 120px;
}

.dashboard-main {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  position: relative;
}

/* === LOADING OVERLAY === */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--light-overlay);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.loading-content {
  background: var(--light-card);
  padding: 40px;
  border-radius: 20px;
  text-align: center;
  max-width: 400px;
  width: 90%;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.spinner-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}

.spinner-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 8px solid var(--loader-border);
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: var(--loader-color) transparent transparent transparent;
}

.loading-content p {
  margin-top: 20px;
  color: var(--subtext-color);
  font-weight: 500;
}

/* === DASHBOARD HEADER === */
.dashboard-header {
  background: var(--card-bg);
  padding: 30px;
  margin-bottom: 30px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.team-info-header {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 30px;
  margin-bottom: 30px;
  align-items: start;
}

@media (max-width: 1024px) {
  .team-info-header {
    grid-template-columns: 1fr;
    gap: 20px;
  }
}

.team-logo-wrapper {
  position: relative;
}

.team-logo-large {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid var(--card-hover-border);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.team-logo-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: var(--meta-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--card-hover-border);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  color: var(--section-icon);
  font-size: 3rem;
}

.team-details {
  flex: 1;
}

.header-title {
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: 800;
  margin-bottom: 15px;
  background: var(--name-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: var(--heading-color);
}

.team-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.team-type, .team-location, .captain-badge, .team-status {
  padding: 6px 16px;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.team-type {
  background: var(--pending-bg);
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.team-location {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.captain-badge {
  background: linear-gradient(135deg, #FFD700, #FFA500);
  color: #8B4513;
  border: 1px solid #FFA500;
}

.team-status.active {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.team-status.inactive {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.team-bio {
  color: var(--subtext-color);
  line-height: 1.6;
  margin-bottom: 20px;
}

.team-stats-quick {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
}

.stat-item i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.stat-item strong {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--heading-color);
}

.stat-item span {
  font-size: 0.9rem;
  color: var(--subtext-color);
}

.team-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 200px;
}

@media (max-width: 1024px) {
  .team-actions {
    flex-direction: row;
    flex-wrap: wrap;
    min-width: auto;
  }
}

.btn-action {
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  width: 100%;
}

@media (max-width: 1024px) {
  .btn-action {
    width: auto;
    flex: 1;
  }
}

.edit-btn {
  background: var(--edit-gradient);
  color: var(--button-color);
  box-shadow: 0 4px 15px var(--edit-shadow);
}

.invite-btn {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  border: 1px solid var(--action-primary-color);
}

.danger-btn {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.btn-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

/* === DASHBOARD TABS === */
.dashboard-tabs {
  display: flex;
  gap: 8px;
  background: var(--meta-bg);
  padding: 8px;
  border-radius: 16px;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tab-btn {
  position: relative;
  background: transparent;
  border: 1px solid transparent;
  color: var(--subtext-color);
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: var(--heading-color);
}

.tab-btn.active {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border-color: var(--tab-active-border);
}

.tab-count {
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  color: var(--button-color);
  font-weight: 600;
}

.tab-btn:not(.active) .tab-count {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
}

.tab-indicator {
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 3px;
  background: var(--section-icon);
  border-radius: 9999px;
  transition: all 0.3s ease;
}

.tab-btn.active .tab-indicator {
  width: 20px;
}

/* === DASHBOARD CONTENT === */
.dashboard-content {
  margin-bottom: 40px;
}

/* === STATS GRID === */
.stats-grid {
  margin-bottom: 40px;
}

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
}

.stats-fade-enter-active,
.stats-fade-leave-active {
  transition: all 0.4s ease;
}

.stats-fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.stats-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.stat-card {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 25px;
  display: flex;
  align-items: center;
  gap: 20px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: var(--card-hover-border);
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: inherit;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.stat-card:hover::before {
  opacity: 1;
}

.stat-card.total { border-left: 4px solid #667eea; }
.stat-card.pending { border-left: 4px solid var(--pending-border); }
.stat-card.confirmed { border-left: 4px solid var(--approved-border); }
.stat-card.revenue { border-left: 4px solid #9C27B0; }

.stat-icon {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  z-index: 1;
}

.stat-card.total .stat-icon { background: linear-gradient(135deg, #667eea, #764ba2); }
.stat-card.pending .stat-icon { background: linear-gradient(135deg, #ffa726, #fb8c00); }
.stat-card.confirmed .stat-icon { background: linear-gradient(135deg, #4CAF50, #45a049); }
.stat-card.revenue .stat-icon { background: linear-gradient(135deg, #9C27B0, #7B1FA2); }

.stat-pulse {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 16px;
  animation: pulse 2s infinite;
}

.stat-info h3 {
  font-size: 2rem;
  margin: 0;
  color: var(--heading-color);
  font-weight: 800;
}

.stat-info p {
  margin: 5px 0 0 0;
  color: var(--subtext-color);
  font-weight: 500;
}

.stat-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  margin-top: 8px;
}

.stat-trend.positive {
  color: var(--approved-color);
}

.stat-trend.negative {
  color: var(--rejected-color);
}

.stat-wave {
  position: absolute;
  bottom: -10px;
  right: -10px;
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 50%;
}

/* === CONTENT GRID === */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 40px;
}

@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

.column {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* === CARDS === */
.card {
  background: var(--card-bg);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 25px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.card-title h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.5rem;
}

.btn-text {
  background: none;
  border: none;
  color: var(--section-icon);
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-text:hover {
  color: var(--heading-color);
  transform: translateX(5px);
}

.card-body {
  padding: 30px;
}

/* === PREFERENCES GRID === */
.preferences-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.preference-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.preference-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--subtext-color);
  font-weight: 500;
}

.preference-value {
  color: var(--heading-color);
  font-weight: 600;
}

.preferred-days-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.day-badge {
  display: inline-block;
  padding: 6px 12px;
  background: linear-gradient(135deg, rgba(58, 134, 255, 0.15), rgba(58, 134, 255, 0.05));
  border: 1px solid rgba(58, 134, 255, 0.3);
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: var(--border-color-hover);
  transition: all 0.3s ease;
}

.day-badge:hover {
  background: linear-gradient(135deg, rgba(58, 134, 255, 0.25), rgba(58, 134, 255, 0.15));
  border-color: var(--border-color-hover);
  transform: translateY(-1px);
}

.skill-badge {
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
}

.skill-badge.beginner {
  background: var(--pending-bg);
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.skill-badge.intermediate {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.skill-badge.advanced {
  background: linear-gradient(135deg, #9C27B0, #7B1FA2);
  color: white;
}

/* === BOOKING CARDS (Reused from pitch dashboard) === */
.booking-list-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

@media (max-width: 768px) {
  .booking-list-container {
    grid-template-columns: 1fr;
  }
}

.booking-list-enter-active,
.booking-list-leave-active {
  transition: all 0.4s ease;
}

.booking-list-enter-from {
  opacity: 0;
  transform: scale(0.9) translateY(20px);
}

.booking-list-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(-20px);
}

.booking-list-move {
  transition: transform 0.4s ease;
}

.booking-card {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.booking-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: var(--card-hover-border);
}

.booking-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: currentColor;
  opacity: 0.3;
}

.booking-card.pending::before { color: var(--pending-border); }
.booking-card.confirmed::before { color: var(--approved-border); }
.booking-card.completed::before { color: #4299e1; }
.booking-card.cancelled::before { color: var(--rejected-border); }

.booking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.booking-id {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: var(--heading-color);
}

.booking-meta {
  display: flex;
  align-items: center;
  gap: 15px;
}

.booking-date {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.85rem;
  color: var(--subtext-color);
}

.booking-status {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
}

.booking-status.pending { 
  background: var(--pending-bg); 
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}
.booking-status.confirmed { 
  background: var(--approved-bg); 
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}
.booking-status.scheduled {
  background: rgba(59, 130, 246, 0.15);
  color: #93c5fd;
  border: 1px solid rgba(59, 130, 246, 0.35);
}
.booking-status.past {
  background: rgba(148, 163, 184, 0.2);
  color: #cbd5e1;
  border: 1px solid rgba(148, 163, 184, 0.35);
}
.booking-status.completed { 
  background: var(--action-success-bg); 
  color: var(--action-success-color);
  border: 1px solid var(--action-success-color);
}
.booking-status.cancelled { 
  background: var(--rejected-bg); 
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}
.booking-status.active { 
  background: var(--approved-bg); 
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}
.booking-status.pending { 
  background: var(--pending-bg); 
  color: var(--pending-color);
  border: 1px solid var(--pending-border);
}

.booking-body {
  margin-bottom: 15px;
}

.booking-customer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.customer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--subtext-color);
}

.customer-info h4 {
  margin: 0 0 4px 0;
  font-size: 0.95rem;
  color: var(--heading-color);
}

.customer-contact {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.8rem;
  color: var(--subtext-color);
}

.customer-contact span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.booking-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.time-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.time-slot, .duration {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--heading-color);
}

.booking-amount {
  text-align: right;
}

.amount-label {
  font-size: 0.75rem;
  color: var(--subtext-color);
  margin-bottom: 2px;
}

.amount-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--heading-color);
}

.booking-notes {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-bottom: 15px;
}

.booking-notes i {
  color: var(--subtext-color);
  margin-top: 2px;
}

.booking-notes p {
  margin: 0;
  font-size: 0.85rem;
  color: var(--subtext-color);
  line-height: 1.4;
}

/* === MATCH SPECIFIC STYLES === */
.match-teams {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
}

.team-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.team-name {
  font-weight: 600;
  color: var(--heading-color);
  text-align: center;
}

.team-score {
  font-size: 2rem;
  font-weight: 800;
  color: var(--heading-color);
}

.vs-divider {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--subtext-color);
  padding: 0 20px;
}

.match-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.venue-info, .time-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--subtext-color);
  font-size: 0.9rem;
}

/* === PLAYER SPECIFIC STYLES === */
.player-stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  margin-bottom: 15px;
}

.stat {
  text-align: center;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.stat strong {
  display: block;
  font-size: 1.2rem;
  color: var(--heading-color);
}

.stat span {
  font-size: 0.8rem;
  color: var(--subtext-color);
}

.player-details {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 15px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 9999px;
  font-size: 0.85rem;
  color: var(--subtext-color);
}

/* === STATS TABLE === */
.table-responsive {
  overflow-x: auto;
}

.stats-table {
  width: 100%;
  border-collapse: collapse;
}

.stats-table th {
  background: rgba(255, 255, 255, 0.05);
  padding: 12px 15px;
  text-align: left;
  color: var(--subtext-color);
  font-weight: 600;
  font-size: 0.9rem;
  border-bottom: 2px solid rgba(255, 255, 255, 0.1);
}

.stats-table td {
  padding: 12px 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--heading-color);
}

.player-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.player-avatar-sm {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--subtext-color);
}

.rating-badge {
  display: inline-block;
  padding: 4px 8px;
  background: var(--approved-bg);
  color: var(--approved-color);
  border-radius: 9999px;
  font-size: 0.85rem;
  font-weight: 600;
}

/* === ACTIVITY LIST === */
.activity-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
}

.activity-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.activity-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.activity-icon.match {
  background: var(--approved-bg);
  color: var(--approved-color);
}

.activity-icon.player {
  background: var(--pending-bg);
  color: var(--pending-color);
}

.activity-content {
  flex: 1;
}

.activity-content p {
  margin: 0 0 4px 0;
  color: var(--heading-color);
  font-size: 0.9rem;
}

.activity-content small {
  color: var(--subtext-color);
  font-size: 0.8rem;
}

/* === BOOKING ACTIONS === */
.booking-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-action {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.3s ease;
  min-width: 100px;
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.btn-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-success {
  background: var(--approved-bg);
  color: var(--approved-color);
  border: 1px solid var(--approved-border);
}

.btn-danger {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.btn-primary {
  background: var(--action-primary-bg);
  color: var(--action-primary-color);
  border: 1px solid var(--action-primary-color);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid var(--border-color-hover);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

/* === EMPTY STATE === */
.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: var(--subtext-color);
}

.empty-state h3 {
  color: var(--heading-color);
  margin: 0 0 10px 0;
}

.empty-state p {
  color: var(--subtext-color);
  margin-bottom: 20px;
}

.btn-refresh {
  background: var(--edit-gradient);
  color: var(--button-color);
  border: none;
  padding: 10px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-refresh:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px var(--edit-shadow);
}

/* === PAGINATION === */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.pagination-btn {
  width: 40px;
  height: 40px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--subtext-color);
}

.pagination-btn:hover:not(:disabled) {
  border-color: var(--section-icon);
  color: var(--heading-color);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 5px;
}

.page-btn {
  width: 40px;
  height: 40px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  color: var(--subtext-color);
}

.page-btn:hover:not(.active) {
  border-color: var(--section-icon);
  color: var(--heading-color);
}

.page-btn.active {
  background: var(--section-icon);
  color: var(--button-color);
  border-color: var(--section-icon);
}

/* === QUICK STATS === */
.quick-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

.stat-item {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-item i {
  font-size: 2rem;
  color: var(--section-icon);
}

.stat-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--heading-color);
  line-height: 1;
}

.stat-label {
  font-size: 0.9rem;
  color: var(--subtext-color);
  margin-top: 5px;
  display: block;
}

/* === STATS OVERVIEW === */
.stats-overview {
  margin-bottom: 40px;
}

.stat-big-card {
  background: var(--card-bg);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-big-card h3 {
  margin: 0 0 30px 0;
  color: var(--heading-color);
  font-size: 1.5rem;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
}

.stats-row .stat-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

/* === SECTION HEADER === */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 20px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-title i {
  font-size: 1.5rem;
  color: var(--section-icon);
}

.section-title h2 {
  margin: 0;
  color: var(--heading-color);
  font-size: 1.5rem;
}

.section-controls {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  width: 200px;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--subtext-color);
}

.search-box input {
  width: 100%;
  padding: 10px 40px 10px 40px;
  border: 2px solid var(--border-color-hover);
  border-radius: 12px;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.05);
  color: var(--heading-color);
}

.search-box input:focus {
  outline: none;
  border-color: var(--section-icon);
  box-shadow: 0 0 0 3px rgba(58, 134, 255, 0.1);
}

.search-box input::placeholder {
  color: var(--subtext-color);
}

.clear-search {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: var(--subtext-color);
  cursor: pointer;
  padding: 4px;
}

/* === FILTER TABS === */
.filter-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 25px;
  flex-wrap: wrap;
}

.filter-tabs .tab-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--light-overlay);
}

.filter-tabs .tab-btn.active {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

/* === CONFIRMATION MODAL === */
.confirmation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--light-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(10px);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.confirmation-content {
  background: var(--light-card);
  border-radius: 20px;
  max-width: 400px;
  width: 90%;
  overflow: hidden;
  animation: modalScale 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.confirmation-header {
  padding: 30px 30px 20px;
  text-align: center;
}

.confirmation-header i {
  font-size: 3rem;
  margin-bottom: 15px;
  display: block;
  color: var(--section-icon);
}

.confirmation-header h3 {
  margin: 0;
  color: var(--heading-color);
}

.confirmation-body {
  padding: 0 30px 30px;
  text-align: center;
}

.confirmation-body p {
  color: var(--subtext-color);
  line-height: 1.6;
  margin-bottom: 15px;
}

.warning-text {
  color: var(--rejected-color) !important;
  font-weight: 600;
}

.booking-info {
  background: var(--meta-bg);
  padding: 15px;
  border-radius: 12px;
  margin-top: 20px;
  text-align: left;
}

.booking-info p {
  margin: 5px 0;
  color: var(--subtext-color);
}

.booking-info strong {
  color: var(--heading-color);
}

.confirmation-actions {
  display: flex;
  gap: 10px;
  padding: 20px 30px;
  background: rgba(0, 0, 0, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.confirmation-actions button {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-confirm {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(245, 101, 101, 0.3);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

/* === ANIMATIONS === */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInRight {
  from { opacity: 0; transform: translateX(100%); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.1); opacity: 0.2; }
  100% { transform: scale(1); opacity: 0.5; }
}

@keyframes modalScale {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

/* === RESPONSIVE DESIGN === */
@media (max-width: 1200px) {
  .dashboard-content {
    grid-template-columns: 1fr;
  }
  
  .booking-list-container {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    margin-top: 60px;
  }
  
  .dashboard-header {
    padding: 20px;
  }
  
  .header-title {
    font-size: 1.8rem;
  }
  
  .stats-container {
    grid-template-columns: 1fr;
  }
  
  .section-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .section-controls {
    flex-direction: column;
  }
  
  .search-box {
    width: 100%;
  }
  
  .filter-tabs {
    overflow-x: auto;
    padding-bottom: 10px;
    justify-content: flex-start;
  }
  
  .tab-btn {
    white-space: nowrap;
  }
  
  .booking-list-container {
    grid-template-columns: 1fr;
  }
  
  .booking-actions {
    flex-direction: column;
  }
  
  .btn-action {
    width: 100%;
  }
  
  .confirmation-actions {
    flex-direction: column;
  }
  
  .quick-stats {
    grid-template-columns: 1fr;
  }
  
  .player-stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .dashboard-main {
    padding: 10px;
  }
  
  .booking-card {
    padding: 15px;
  }
  
  .booking-details {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .booking-amount {
    text-align: left;
  }
  
  .toast {
    min-width: 280px;
  }
  
  .match-teams {
    flex-direction: column;
    gap: 15px;
  }
  
  .vs-divider {
    padding: 10px 0;
  }
  
  .preferences-grid {
    grid-template-columns: 1fr;
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    animation-delay: 0ms !important;
    transition-delay: 0ms !important;
  }
  
  .booking-card,
  .stat-card,
  .tab-btn,
  .btn-action,
  .edit-btn {
    transition: none !important;
  }
  
  .booking-card:hover,
  .stat-card:hover {
    transform: none !important;
  }
}

/* Challenges Section Styles */
.challenges-section {
  margin-top: 30px;
  margin-bottom: 30px;
}

.badge-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: linear-gradient(135deg, var(--border-color-hover), #4a9eff);
  color: white;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
  margin-left: 8px;
}

.challenges-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.challenge-card {
  border-left: 4px solid var(--border-color-hover);
  transition: all 0.3s ease;
}

.challenge-card:hover {
  transform: translateX(4px);
  box-shadow: 0 4px 16px rgba(58, 134, 255, 0.2);
}

.challenge-info {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.challenger-team {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: var(--background-color2);
  border-radius: 12px;
}

.team-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--background-color3);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid var(--border-color-hover);
}

.team-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-color2);
  font-size: 20px;
}

.team-details {
  flex: 1;
}

.team-name {
  font-weight: 700;
  font-size: 16px;
  color: var(--text-color1);
  margin-bottom: 4px;
}

.challenge-label {
  font-size: 13px;
  color: var(--text-color2);
  font-style: italic;
}

.challenge-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--background-color2);
  border-radius: 12px;
}

.challenge-details .detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text-color2);
}

.challenge-details .detail-item i {
  color: var(--border-color-hover);
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.challenge-details .detail-item--venue {
  align-items: flex-start;
}

.challenge-details .venue-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.challenge-details .venue-text {
  line-height: 1.4;
}

.challenge-details .venue-location-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--border-color-hover);
  text-decoration: none;
}

.challenge-details .venue-location-link:hover {
  text-decoration: underline;
  color: var(--heading-color);
}

.challenges-empty {
  text-align: center;
  padding: 48px 20px;
}

.challenge-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-success {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
}

.btn-success:hover:not(:disabled) {
  background: linear-gradient(135deg, #45a049, #3d8b40);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
}

.btn-danger {
  background: linear-gradient(135deg, #f44336, #d32f2f);
  color: white;
  border: none;
}

.btn-danger:hover:not(:disabled) {
  background: linear-gradient(135deg, #d32f2f, #c62828);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.4);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  gap: 16px;
}

.spinner-small {
  width: 32px;
  height: 32px;
  border: 3px solid var(--background-color3);
  border-top-color: var(--border-color-hover);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.challenge-list-enter-active,
.challenge-list-leave-active {
  transition: all 0.4s ease;
}

.challenge-list-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.challenge-list-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.challenge-list-move {
  transition: transform 0.4s ease;
}

@media (max-width: 768px) {
  .challenge-actions {
    flex-direction: column;
  }
  
  .challenge-actions .btn-action {
    width: 100%;
  }
  
  .challenger-team {
    flex-direction: column;
    text-align: center;
  }
}
</style>